import React, { useState } from 'react';

import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';

import type { ComparisonData } from './comparisons';
import { metricText } from './metrics';

type Props = {
  title: string;
  dimension: string;
  dimensionLabel: string;
  data: ComparisonData;
  source: string;
  range: string;
  onSelect: (label: string) => void;
  onCsv: () => void;
};

export default function ProductPerformanceComparisons({
  title,
  dimension,
  dimensionLabel,
  data,
  source,
  range,
  onSelect,
  onCsv,
}: Props) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const safePage = Math.min(page, Math.max(0, Math.ceil(data.rows.length / pageSize) - 1));
  return (
    <Paper variant="outlined" sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ p: 2 }}>
        <Typography component="h2" variant="h6">
          {title}
        </Typography>
        <Button size="small" onClick={onCsv} disabled={!data.rows.length}>
          CSV
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1 }}>
        {dimension === 'product'
          ? 'Change compares each product with the product above it in the same month, as the workbook does.'
          : 'Change compares each month with the previous month.'}{' '}
        Select a name to filter every section.
      </Typography>
      <TableContainer>
        <Table size="small" stickyHeader aria-label={title} sx={{ fontVariantNumeric: 'tabular-nums' }}>
          <TableHead>
            <TableRow>
              <TableCell>{dimensionLabel}</TableCell>
              {data.months.map((month) => (
                <TableCell key={month} colSpan={2} align="center">
                  {month}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              {data.months.map((month) => (
                <React.Fragment key={month}>
                  <TableCell align="right">Sales AUD</TableCell>
                  <TableCell align="right">Change</TableCell>
                </React.Fragment>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.rows.slice(safePage * pageSize, (safePage + 1) * pageSize).map((row) => (
              <TableRow key={row.label} hover>
                <TableCell component="th" scope="row" sx={{ minWidth: 190 }}>
                  <Button
                    size="small"
                    onClick={() => onSelect(row.label)}
                    sx={{ px: 0, textAlign: 'left', textTransform: 'none' }}
                  >
                    {row.label}
                  </Button>
                </TableCell>
                {data.months.map((month) => (
                  <React.Fragment key={month}>
                    <TableCell align="right" sx={{ minWidth: 110, whiteSpace: 'nowrap' }}>
                      {metricText(row.sales[month])}
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 90, whiteSpace: 'nowrap' }}>
                      {metricText(row.changes[month], 'percent')}
                    </TableCell>
                  </React.Fragment>
                ))}
              </TableRow>
            ))}
            {!data.rows.length && (
              <TableRow>
                <TableCell colSpan={1 + data.months.length * 2}>No sales match these filters.</TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell component="th" scope="row">
                Grand total
              </TableCell>
              {data.months.map((month) => (
                <React.Fragment key={month}>
                  <TableCell align="right">{metricText(data.total.sales[month])}</TableCell>
                  <TableCell align="right">{metricText(data.total.changes[month], 'percent')}</TableCell>
                </React.Fragment>
              ))}
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={data.rows.length}
        page={safePage}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(_, next) => setPage(next)}
        onRowsPerPageChange={(event) => {
          setPageSize(Number(event.target.value));
          setPage(0);
        }}
      />
      <Box sx={{ px: 2, pb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {source} · {range} · Sydney transaction months. The first month and a zero base have no change.
        </Typography>
      </Box>
    </Paper>
  );
}
